import {
  GameState,
  IMove,
  Position,
  MoveType,
  PieceType,
  EffectType,
} from "../../types";
import { GameStateManager } from "../../Managers/GameStateManager";
import { MovementManager } from "../../Managers/MovementManager";
import { HeuristicInterface } from "../../heuristics/HeuristicInterface";
import { ZodiacEngine } from "../../zodiacEngine";
import { directions, animalPieceTypes } from "../../const";

/**
 * Check if there are enemy animal pieces within Virus movement range from a position.
 * Virus moves 2 squares in 8 directions (or 1 square at board edge).
 * Used to allow friendly infections only when they serve as stepping stones to enemies.
 */
function hasEnemyVirusTargetFromPosition(
  position: Position,
  board: { Pieces: { type: string; isLight: boolean }[] }[][],
  virusIsLight: boolean
): boolean {
  for (const direction of directions) {
    const twoAway = {
      row: position.row + direction[1] * 2,
      col: position.col + direction[0] * 2,
    };
    if (twoAway.row >= 0 && twoAway.row < board.length && twoAway.col >= 0 && twoAway.col < board[0].length) {
      if (board[twoAway.row][twoAway.col].Pieces.some(p => animalPieceTypes.includes(p.type as PieceType) && p.isLight !== virusIsLight)) {
        return true;
      }
    } else {
      const oneAway = {
        row: position.row + direction[1],
        col: position.col + direction[0],
      };
      if (oneAway.row >= 0 && oneAway.row < board.length && oneAway.col >= 0 && oneAway.col < board[0].length) {
        if (board[oneAway.row][oneAway.col].Pieces.some(p => animalPieceTypes.includes(p.type as PieceType) && p.isLight !== virusIsLight)) {
          return true;
        }
      }
    }
  }
  return false;
}

/**
 * CENTRALIZED MOVE GENERATION SERVICE
 *
 * This service consolidates all move generation logic and eliminates the need
 * to pass getAllAvailableMoves function around as a parameter everywhere.
 *
 * Benefits:
 * - Single source of truth for move generation
 * - Eliminates parameter passing bloat
 * - Easier to test and maintain
 * - Consistent filtering and processing logic
 */
export class MCTSMoveGenerationService {
  private static instance: MCTSMoveGenerationService | null = null;
  private heuristic: HeuristicInterface | null = null;
  private gameEngine: ZodiacEngine | null = null;

  private constructor() {}

  /**
   * Get singleton instance
   */
  public static getInstance(): MCTSMoveGenerationService {
    if (!MCTSMoveGenerationService.instance) {
      MCTSMoveGenerationService.instance = new MCTSMoveGenerationService();
    }
    return MCTSMoveGenerationService.instance;
  }

  /**
   * Set the heuristic for move filtering
   */
  public setHeuristic(heuristic: HeuristicInterface | null): void {
    this.heuristic = heuristic;
  }

  /**
   * Set the game engine for heuristic policy filtering
   */
  public setGameEngine(gameEngine: ZodiacEngine | null): void {
    this.gameEngine = gameEngine;
  }

  /**
   * COMPREHENSIVE MOVE GENERATION
   * The main method that generates all available moves for the current player
   * @param gameState The current game state
   * @param useAdvancedFiltering Whether to apply advanced move filtering (default: true)
   * @returns Array of all available moves with their positions
   */
  public getAllMoves(
    gameState: GameState,
    useAdvancedFiltering: boolean = true
  ): { fromPosition: Position; move: IMove }[] {
    const gameStateManager = new GameStateManager();
    gameStateManager.setGameState(gameState);
    return this.getAllMovesWithGSM(gameStateManager, useAdvancedFiltering);
  }

  /**
   * Generate moves using existing GameStateManager
   * @param gameStateManager The game state manager
   * @param useAdvancedFiltering Whether to apply advanced move filtering
   * @returns Array of all available moves with their positions
   */
  public getAllMovesWithGSM(
    gameStateManager: GameStateManager,
    useAdvancedFiltering: boolean = true
  ): { fromPosition: Position; move: IMove }[] {
    const allMoves: { fromPosition: Position; move: IMove }[] = [];
    const state = gameStateManager.getGameState();
    const isLightTurn = state.isLightTurn;
    const isInBonusTurn = !!state.activeBonusTurnPieceId;

    if (isInBonusTurn) {
      // During bonus turns, only generate moves for the active bonus turn piece
      return this.getBonusTurnMoves(gameStateManager, useAdvancedFiltering);
    }

    // Create MovementManager instance for proper rule checking
    const movementManager = new MovementManager(gameStateManager);

    // Generate all raw moves for normal turns
    for (let row = 0; row < state.board.length; row++) {
      for (let col = 0; col < state.board[row].length; col++) {
        const position: Position = { row, col };
        const pieces = gameStateManager.getPiecesAt(position);

        for (const piece of pieces) {
          // Allow control if piece is the current player's color, or if it is zombified
          const isOwnColor = piece.isLight === isLightTurn;
          const isZombified = piece.zombifiedStatus?.isZombified || false;
          if (isOwnColor || isZombified) {
            // Use the proper MovementManager method that includes entanglement, poison, and other checks
            let validMoves = movementManager.getValidMoves(position, piece);

            // Apply filtering if requested
            if (useAdvancedFiltering) {
              validMoves = this.applyMoveFiltering(
                validMoves,
                piece,
                position,
                state
              );
            }

            for (const move of validMoves) {
              allMoves.push({
                fromPosition: position,
                move: move,
              });
            }
          }
        }
      }
    }

    return allMoves;
  }

  /**
   * Apply move filtering logic
   * @param moves The raw moves to filter
   * @param piece The piece making the moves
   * @param position The piece's position
   * @param state The current game state
   * @returns Filtered moves
   */
  private applyMoveFiltering(
    moves: IMove[],
    piece: any,
    position: Position,
    state: GameState
  ): IMove[] {
    let filteredMoves = moves;

    // Filter out problematic move types
    filteredMoves = filteredMoves.filter((move) => {
      // No pure rotation moves
      if (move.moveType === MoveType.rotate) {
        return false;
      }

      // No "no move" unless it's penguin rotation or octopus entangle
      if (move.moveType === MoveType.noMove) {
        const isPenguinRotation = move.sideEffects?.some(
          (sideEffect) => sideEffect.effect.type === EffectType.rotate
        );
        const isOctopusEntangle = move.sideEffects?.some(
          (sideEffect) => sideEffect.effect.type === EffectType.entangle
        );
        if (!isPenguinRotation && !isOctopusEntangle) {
          return false;
        }
      }

      // Virus endTurn is only legal during bonus turns (infection chain)
      if (move.moveType === MoveType.endTurn && piece.type === PieceType.Virus && !state.activeBonusTurnPieceId) {
        return false;
      }

      // Smart Virus friendly-fire prevention:
      // - NEVER infect own king (suicidal - virus captures king on departure)
      // - Allow friendly infection ONLY if enemies are reachable from destination (stepping stone)
      if (move.moveType === MoveType.infect && piece.type === PieceType.Virus
          && move.toPiece && move.toPiece.isLight === piece.isLight) {
        if (move.toPiece.isKing) return false; // NEVER infect own king
        return hasEnemyVirusTargetFromPosition(move.position, state.board, piece.isLight);
      }

      // Special Rhino filtering
      if (piece.type === PieceType.Rhino) {
        if (move.moveType === MoveType.lane) {
          return false;
        }
        const rowDiff = Math.abs(move.position.row - position.row);
        const colDiff = Math.abs(move.position.col - position.col);
        // No horizontal-only movement for rhinos
        if (rowDiff === 0 && colDiff > 0) {
          return false;
        }
      }

      return true;
    });

    // Convert tree captures to housing moves
    filteredMoves = filteredMoves.map((move) => {
      if (move.toPiece?.type === PieceType.Tree) {
        const treeIsLight = move.toPiece.isLight;
        const movingPieceIsLight = piece.isLight;
        if (
          move.moveType === MoveType.capture &&
          treeIsLight === movingPieceIsLight
        ) {
          return {
            ...move,
            moveType: MoveType.house,
          };
        }
      }
      return move;
    });

    // Apply heuristic policy filter if available
    if (this.heuristic && this.heuristic.policyFilter && this.gameEngine) {
      filteredMoves = this.heuristic.policyFilter(
        this.gameEngine,
        state,
        position,
        filteredMoves
      );
    }

    return filteredMoves;
  }

  /**
   * Generate moves specifically for bonus turn pieces
   * @param gameStateManager The game state manager
   * @param useAdvancedFiltering Whether to apply advanced move filtering
   * @returns Array of moves for the active bonus turn piece only
   */
  private getBonusTurnMoves(
    gameStateManager: GameStateManager,
    useAdvancedFiltering: boolean = true
  ): { fromPosition: Position; move: IMove }[] {
    const allMoves: { fromPosition: Position; move: IMove }[] = [];
    const state = gameStateManager.getGameState();
    const activeBonusTurnPieceId = state.activeBonusTurnPieceId;

    if (!activeBonusTurnPieceId) {
      return allMoves; // Safety check
    }

    // Create MovementManager instance for proper rule checking
    const movementManager = new MovementManager(gameStateManager);

    // Find the active bonus turn piece on the board
    for (let row = 0; row < state.board.length; row++) {
      for (let col = 0; col < state.board[row].length; col++) {
        const position: Position = { row, col };
        const pieces = gameStateManager.getPiecesAt(position);

        for (const piece of pieces) {
          // Only generate moves for the specific bonus turn piece
          if (piece.id === activeBonusTurnPieceId) {
            // Use the proper MovementManager method that includes entanglement, poison, and other checks
            let validMoves = movementManager.getValidMoves(position, piece);

            // Apply filtering if requested
            if (useAdvancedFiltering) {
              validMoves = this.applyMoveFiltering(
                validMoves,
                piece,
                position,
                state
              );
            }

            // Filter to only bonus turn appropriate moves
            validMoves = this.filterBonusTurnMoves(validMoves, piece);

            for (const move of validMoves) {
              allMoves.push({
                fromPosition: position,
                move: move,
              });
            }

            // Found the piece, no need to continue searching
            return allMoves;
          }
        }
      }
    }

    // If we can't find the bonus turn piece, return empty array
    return allMoves;
  }

  /**
   * Filter moves to only those appropriate for bonus turns
   * @param moves The moves to filter
   * @param piece The piece making the moves
   * @returns Filtered moves appropriate for bonus turns
   */
  private filterBonusTurnMoves(moves: IMove[], piece: any): IMove[] {
    // Filter based on piece type and bonus turn rules
    switch (piece.type) {
      case PieceType.Monkey:
        // Monkey bonus turns allow swing, consume, leap, capture, regular moves, and end turn
        return moves.filter(
          (move) =>
            move.moveType === MoveType.swing ||
            move.moveType === MoveType.consume ||
            move.moveType === MoveType.endTurn ||
            move.moveType === MoveType.leap ||
            move.moveType === MoveType.capture ||
            move.moveType === MoveType.regular ||
            move.moveType === MoveType.house ||
            move.moveType === MoveType.traverse
        );

      case PieceType.Virus:
        // Virus bonus turns allow infect or end turn moves
        return moves.filter(
          (move) =>
            move.moveType === MoveType.infect ||
            move.moveType === MoveType.endTurn
        );

      case PieceType.Penguin:
        // Penguin bonus turns allow rotation or end turn moves
        return moves.filter(
          (move) =>
            (move.moveType === MoveType.noMove && // Rotation moves are noMove with rotate side effects
              move.sideEffects?.some(
                (se) => se.effect.type === EffectType.rotate
              )) ||
            move.moveType === MoveType.endTurn
        );

      default:
        // For other pieces, allow all moves (shouldn't happen in normal gameplay)
        return moves;
    }
  }

  /**
   * SIMPLE MOVE GENERATION (for core MCTS algorithm)
   * Generates moves without advanced filtering - used in simulations
   * @param gameStateManager The game state manager
   * @returns Array of all basic moves
   */
  public getSimpleMoves(
    gameStateManager: GameStateManager
  ): { fromPosition: Position; move: IMove }[] {
    return this.getAllMovesWithGSM(gameStateManager, false);
  }

  /**
   * STATIC HELPER METHODS for backward compatibility
   */

  /**
   * Static method for backward compatibility - generates all moves
   */
  public static getAllAvailableMoves(
    gameStateManager: GameStateManager
  ): { fromPosition: Position; move: IMove }[] {
    return MCTSMoveGenerationService.getInstance().getAllMovesWithGSM(
      gameStateManager
    );
  }

  /**
   * Static method for backward compatibility - generates simple moves
   */
  public static getSimpleAvailableMoves(
    gameStateManager: GameStateManager
  ): { fromPosition: Position; move: IMove }[] {
    return MCTSMoveGenerationService.getInstance().getSimpleMoves(
      gameStateManager
    );
  }
}
